// src/relations.ts
import { createSchema } from "@rocicorp/zero";
import {
  createTableRelationsHelpers,
  getTableName as getTableName3,
  is as is2,
  One,
  Relations,
  Table as Table2
} from "drizzle-orm";

// src/db.ts
import { getTableName, is } from "drizzle-orm";
import { getTableConfig, PgTable } from "drizzle-orm/pg-core";
var getTableConfigForDatabase = (table) => {
  if (is(table, PgTable)) {
    return getTableConfig(table);
  }
  throw new Error(
    `drizzle-zero: Unsupported table type: ${getTableName(table)}. Only Postgres tables are supported.`
  );
};

// src/tables.ts
import {
  boolean as zeroBoolean,
  enumeration as zeroEnumeration,
  json as zeroJson,
  number as zeroNumber,
  string as zeroString,
  table as zeroTable
} from "@rocicorp/zero";
import { getTableColumns, getTableName as getTableName2 } from "drizzle-orm";

// src/drizzle-to-zero.ts
var drizzleDataTypeToZeroType = {
  number: "number",
  bigint: "number",
  boolean: "boolean",
  date: "number"
};
var drizzleColumnTypeToZeroType = {
  PgText: "string",
  PgChar: "string",
  PgVarchar: "string",
  PgUUID: "string",
  PgEnumColumn: "string",
  PgJsonb: "json",
  PgJson: "json",
  PgNumeric: "number",
  PgDateString: "number",
  PgTimestampString: "number"
};

// src/util.ts
function typedEntries(obj) {
  return Object.entries(obj);
}

// src/tables.ts
import { toCamelCase, toSnakeCase } from "drizzle-orm/casing";
var createZeroTableBuilder = (tableName, table, columns, casing) => {
  const actualTableName = getTableName2(table);
  const tableColumns = getTableColumns(table);
  const tableConfig = getTableConfigForDatabase(table);
  const primaryKeysFromColumns = [];
  const columnsMapped = typedEntries(tableColumns).reduce(
    (acc, [key, column]) => {
      const columnConfig = columns[key];
      if (columnConfig === false) {
        return acc;
      }
      const resolvedColumnName = !column.keyAsName || casing === void 0 ? column.name : casing === "camelCase" ? toCamelCase(column.name) : toSnakeCase(column.name);
      if (typeof columnConfig !== "boolean" && typeof columnConfig !== "object" && typeof columnConfig !== "undefined") {
        throw new Error(
          `drizzle-zero: Invalid column config for column ${resolvedColumnName} - expected boolean or ColumnBuilder but was ${typeof columnConfig}`
        );
      }
      const isColumnBuilder = (value) => typeof value === "object" && value !== null && "schema" in value;
      const type = drizzleColumnTypeToZeroType[column.columnType] ?? drizzleDataTypeToZeroType[column.dataType] ?? null;
      if (type === null) {
        throw new Error(
          `drizzle-zero: Unsupported column type: ${column.columnType} (${column.dataType}). It must be supported by Zero, e.g.: ${Object.keys({ ...drizzleDataTypeToZeroType, ...drizzleColumnTypeToZeroType }).join(" | ")}`
        );
      }
      const isColumnOptional = typeof columnConfig === "boolean" ? column.hasDefault && column.defaultFn === void 0 ? true : !column.notNull : isColumnBuilder(columnConfig) ? columnConfig.schema.optional : false;
      if (column.primary) {
        primaryKeysFromColumns.push(String(key));
      }
      if (columnConfig && typeof columnConfig !== "boolean") {
        return {
          ...acc,
          [key]: columnConfig
        };
      }
      const schemaValue = column.enumValues ? zeroEnumeration() : type === "string" ? zeroString() : type === "number" ? zeroNumber() : type === "json" ? zeroJson() : zeroBoolean();
      const schemaValueWithFrom = resolvedColumnName !== key ? schemaValue.from(resolvedColumnName) : schemaValue;
      return {
        ...acc,
        [key]: isColumnOptional ? schemaValueWithFrom.optional() : schemaValueWithFrom
      };
    },
    {}
  );
  const primaryKeys = [
    ...primaryKeysFromColumns,
    ...tableConfig.primaryKeys.flatMap(
      (k) => k.columns.map(
        (c) => getDrizzleColumnKeyFromColumnName({
          columnName: c.name,
          table: c.table
        })
      )
    )
  ];
  if (!primaryKeys.length) {
    throw new Error(
      `drizzle-zero: No primary keys found in table - ${actualTableName}. Did you forget to define a primary key?`
    );
  }
  const resolvedTableName = tableConfig.schema ? `${tableConfig.schema}.${actualTableName}` : actualTableName;
  const zeroBuilder = zeroTable(tableName);
  const zeroBuilderWithFrom = resolvedTableName !== tableName ? zeroBuilder.from(resolvedTableName) : zeroBuilder;
  return zeroBuilderWithFrom.columns(columnsMapped).primaryKey(
    ...primaryKeys
  );
};
var getDrizzleColumnKeyFromColumnName = ({
  columnName,
  table
}) => {
  const tableColumns = getTableColumns(table);
  return typedEntries(tableColumns).find(
    ([_name, column]) => column.name === columnName
  )?.[0];
};

// src/relations.ts
var createZeroSchema = (schema, schemaConfig) => {
  let tables = [];
  for (const [tableName, tableOrRelations] of typedEntries(schema)) {
    if (is2(tableOrRelations, Table2)) {
      const table = tableOrRelations;
      const tableConfig = schemaConfig.tables[tableName];
      if (!tableConfig) {
        continue;
      }
      const tableSchema = createZeroTableBuilder(
        String(tableName),
        table,
        tableConfig,
        schemaConfig?.casing
      );
      tables.push(tableSchema);
    }
  }
  let relationships = {};
  if (schemaConfig.manyToMany) {
    for (const [sourceTableName, manyConfig] of Object.entries(
      schemaConfig.manyToMany
    )) {
      if (!manyConfig) continue;
      for (const [
        relationName,
        [junctionTableNameOrObject, destTableNameOrObject]
      ] of Object.entries(manyConfig)) {
        if (typeof junctionTableNameOrObject === "string" && typeof destTableNameOrObject === "string") {
          const junctionTableName = junctionTableNameOrObject;
          const destTableName = destTableNameOrObject;
          const sourceTable = typedEntries(schema).find(
            ([tableName, tableOrRelations]) => is2(tableOrRelations, Table2) && tableName === sourceTableName
          )?.[1];
          const destTable = typedEntries(schema).find(
            ([tableName, tableOrRelations]) => is2(tableOrRelations, Table2) && tableName === destTableName
          )?.[1];
          const junctionTable = typedEntries(schema).find(
            ([tableName, tableOrRelations]) => is2(tableOrRelations, Table2) && tableName === junctionTableName
          )?.[1];
          if (!sourceTable || !destTable || !junctionTable || !is2(sourceTable, Table2) || !is2(destTable, Table2) || !is2(junctionTable, Table2)) {
            throw new Error(
              `drizzle-zero: Invalid many-to-many configuration for ${String(sourceTableName)}.${relationName}: Could not find ${!sourceTable ? "source" : !destTable ? "destination" : "junction"} table`
            );
          }
          const sourceJunctionFields = findForeignKeySourceAndDestFields(
            schema,
            {
              sourceTable,
              referencedTableName: getTableName3(junctionTable)
            }
          );
          const junctionDestFields = findForeignKeySourceAndDestFields(schema, {
            sourceTable: destTable,
            referencedTableName: getTableName3(junctionTable)
          });
          if (!sourceJunctionFields.sourceFieldNames.length || !junctionDestFields.sourceFieldNames.length || !junctionDestFields.destFieldNames.length || !sourceJunctionFields.destFieldNames.length) {
            throw new Error(
              `drizzle-zero: Invalid many-to-many configuration for ${String(sourceTableName)}.${relationName}: Could not find foreign key relationships in junction table ${junctionTableName}`
            );
          }
          if (!schemaConfig.tables[junctionTableName] || !schemaConfig.tables[sourceTableName] || !schemaConfig.tables[destTableName]) {
            continue;
          }
          relationships[sourceTableName] = {
            ...relationships?.[sourceTableName] ?? {},
            [relationName]: [
              {
                sourceField: sourceJunctionFields.sourceFieldNames,
                destField: sourceJunctionFields.destFieldNames,
                destSchema: junctionTableName,
                cardinality: "many"
              },
              {
                sourceField: junctionDestFields.destFieldNames,
                destField: junctionDestFields.sourceFieldNames,
                destSchema: destTableName,
                cardinality: "many"
              }
            ]
          };
        } else {
          const junctionTableName = junctionTableNameOrObject?.destTable ?? null;
          const junctionSourceField = junctionTableNameOrObject?.sourceField ?? null;
          const junctionDestField = junctionTableNameOrObject?.destField ?? null;
          const destTableName = destTableNameOrObject?.destTable ?? null;
          const destSourceField = destTableNameOrObject?.sourceField ?? null;
          const destDestField = destTableNameOrObject?.destField ?? null;
          if (!junctionSourceField || !junctionDestField || !destSourceField || !destDestField || !junctionTableName || !destTableName) {
            throw new Error(
              `drizzle-zero: Invalid many-to-many configuration for ${String(sourceTableName)}.${relationName}: Not all required fields were provided.`
            );
          }
          if (!schemaConfig.tables[junctionTableName] || !schemaConfig.tables[sourceTableName] || !schemaConfig.tables[destTableName]) {
            continue;
          }
          relationships[sourceTableName] = {
            ...relationships?.[sourceTableName] ?? {},
            [relationName]: [
              {
                sourceField: junctionSourceField,
                destField: junctionDestField,
                destSchema: junctionTableName,
                cardinality: "many"
              },
              {
                sourceField: destSourceField,
                destField: destDestField,
                destSchema: destTableName,
                cardinality: "many"
              }
            ]
          };
        }
      }
    }
  }
  for (const [_relationName, tableOrRelations] of typedEntries(schema)) {
    if (is2(tableOrRelations, Relations)) {
      const actualTableName = getTableName3(tableOrRelations.table);
      const tableName = getDrizzleKeyFromTableName({
        schema,
        tableName: actualTableName
      });
      const relationsConfig = getRelationsConfig(tableOrRelations);
      for (const relation of Object.values(relationsConfig)) {
        let sourceFieldNames = [];
        let destFieldNames = [];
        if (is2(relation, One)) {
          sourceFieldNames = relation?.config?.fields?.map(
            (f) => getDrizzleColumnKeyFromColumnName({
              columnName: f?.name,
              table: f.table
            })
          ) ?? [];
          destFieldNames = relation?.config?.references?.map(
            (f) => getDrizzleColumnKeyFromColumnName({
              columnName: f?.name,
              table: f.table
            })
          ) ?? [];
        }
        if (!sourceFieldNames.length || !destFieldNames.length) {
          if (relation.relationName) {
            const sourceAndDestFields = findNamedSourceAndDestFields(
              schema,
              relation
            );
            sourceFieldNames = sourceAndDestFields.sourceFieldNames;
            destFieldNames = sourceAndDestFields.destFieldNames;
          } else {
            const sourceAndDestFields = findForeignKeySourceAndDestFields(
              schema,
              relation
            );
            sourceFieldNames = sourceAndDestFields.sourceFieldNames;
            destFieldNames = sourceAndDestFields.destFieldNames;
          }
        }
        if (!sourceFieldNames.length || !destFieldNames.length) {
          throw new Error(
            `drizzle-zero: No relationship found for: ${relation.fieldName} (${is2(relation, One) ? "One" : "Many"} from ${String(tableName)} to ${relation.referencedTableName}). Did you forget to define foreign keys${relation.relationName ? ` for named relation "${relation.relationName}"` : ""}?`
          );
        }
        const referencedTableKey = getDrizzleKeyFromTableName({
          schema,
          tableName: relation.referencedTableName
        });
        if (!schemaConfig.tables[tableName] || !schemaConfig.tables[referencedTableKey]) {
          continue;
        }
        if (relationships[tableName]?.[relation.fieldName]) {
          throw new Error(
            `drizzle-zero: Duplicate relationship found for: ${relation.fieldName} (from ${String(tableName)} to ${relation.referencedTableName}).`
          );
        }
        relationships[tableName] = {
          ...relationships?.[tableName] ?? {},
          [relation.fieldName]: [
            {
              sourceField: sourceFieldNames,
              destField: destFieldNames,
              destSchema: getDrizzleKeyFromTableName({
                schema,
                tableName: relation.referencedTableName
              }),
              cardinality: is2(relation, One) ? "one" : "many"
            }
          ]
        };
      }
    }
  }
  return createSchema({
    tables,
    relationships: Object.entries(relationships).map(([key, value]) => ({
      name: key,
      relationships: value
    }))
  });
};
var findForeignKeySourceAndDestFields = (schema, relation) => {
  for (const tableOrRelations of Object.values(schema)) {
    if (is2(tableOrRelations, Table2)) {
      const tableName = getTableName3(tableOrRelations);
      if (tableName === relation.referencedTableName) {
        const tableConfig = getTableConfigForDatabase(tableOrRelations);
        for (const foreignKey of tableConfig.foreignKeys) {
          const reference = foreignKey.reference();
          const foreignTableName = getDrizzleKeyFromTableName({
            schema,
            tableName: getTableName3(reference.foreignTable)
          });
          const sourceTableName = getDrizzleKeyFromTableName({
            schema,
            tableName: getTableName3(relation.sourceTable)
          });
          if (foreignTableName === sourceTableName) {
            return {
              sourceFieldNames: reference.foreignColumns.map(
                (c) => getDrizzleColumnKeyFromColumnName({
                  columnName: c.name,
                  table: c.table
                })
              ),
              destFieldNames: reference.columns.map(
                (c) => getDrizzleColumnKeyFromColumnName({
                  columnName: c.name,
                  table: c.table
                })
              )
            };
          }
        }
      }
    }
  }
  return {
    sourceFieldNames: [],
    destFieldNames: []
  };
};
var findNamedSourceAndDestFields = (schema, relation) => {
  for (const tableOrRelations of Object.values(schema)) {
    if (is2(tableOrRelations, Relations)) {
      const relationsConfig = getRelationsConfig(tableOrRelations);
      for (const relationConfig of Object.values(relationsConfig)) {
        if (is2(relationConfig, One) && relationConfig.relationName === relation.relationName) {
          return {
            destFieldNames: relationConfig.config?.fields?.map(
              (f) => getDrizzleColumnKeyFromColumnName({
                columnName: f.name,
                table: f.table
              })
            ) ?? [],
            sourceFieldNames: relationConfig.config?.references?.map(
              (f) => getDrizzleColumnKeyFromColumnName({
                columnName: f.name,
                table: f.table
              })
            ) ?? []
          };
        }
      }
    }
  }
  return {
    sourceFieldNames: [],
    destFieldNames: []
  };
};
var getRelationsConfig = (relations) => {
  return relations.config(
    createTableRelationsHelpers(relations.table)
  );
};
var getDrizzleKeyFromTableName = ({
  schema,
  tableName
}) => {
  return typedEntries(schema).find(
    ([_name, tableOrRelations]) => is2(tableOrRelations, Table2) && getTableName3(tableOrRelations) === tableName
  )?.[0];
};
export {
  createZeroSchema,
  createZeroTableBuilder
};
